// field-group.component.ts
import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

// Kendo UI imports
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { CheckboxComponent } from '../../checkbox/checkbox.component';
import { DatepickerComponent } from '../../datepicker/datepicker.component';
import { FileUploadComponent } from '../../file-upload/file-upload.component';
import { InputComponent } from '../../input/input.component';
import { RadioComponent } from '../../radio/radio.component';
import { SelectComponent } from '../../select/select.component';
import { TextareaComponent } from '../../text-area/text-area.component';
import {
  FieldGroupConfig,
  FieldGroupInstance,
  FormField,
  DataSourceConfig,
} from '../models/dynamic-form.model';
import { FieldDataService } from './service/field-data.service';
import { TimepickerComponent } from '../../timepicker/timepicker.component';

@Component({
  selector: 'app-field-group',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonsModule,
    InputComponent,
    SelectComponent,
    TextareaComponent,
    CheckboxComponent,
    RadioComponent,
    FileUploadComponent,
    DatepickerComponent,
    TimepickerComponent,
  ],
  templateUrl: './field-group.component.html',
  styleUrls: ['./field-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldGroupComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fieldDataService = inject(FieldDataService);
  private readonly destroy$ = new Subject<void>();

  @Input() config!: FieldGroupConfig;
  @Input() parentForm!: FormGroup;
  @Input() formArrayName!: string;

  instances: FieldGroupInstance[] = [];
  private instanceIdCounter = 0;
  private readonly fieldDataSourceConfigs = new Map<string, DataSourceConfig>();
  private previousInstanceValues: { [key: number]: any } = {};

  // Track which instances have been initialized to avoid duplicate loading
  private initializedInstances = new Set<number>();

  get formArray(): FormArray {
    return this.parentForm.get(this.formArrayName) as FormArray;
  }

  get canAddInstance(): boolean {
    if (!this.config.maxInstances) return true;
    return this.instances.length < this.config.maxInstances;
  }

  get canRemoveInstance(): boolean {
    const minInstances = this.config.minInstances ?? 0;
    return this.instances.length > minInstances;
  }

  get showErrorState(): boolean {
    return this.formArray?.invalid && this.formArray?.touched;
  }

  get currentErrorMessage(): string {
    const minInstances = this.config.minInstances ?? 0;
    const maxInstances = this.config.maxInstances;

    if (this.instances.length < minInstances) {
      return `At least ${minInstances} item(s) required`;
    }

    if (maxInstances && this.instances.length > maxInstances) {
      return `Maximum ${maxInstances} items allowed`;
    }

    return 'Please fix the errors in this section';
  }

  ngOnInit(): void {
    this.initializeFieldGroup();
    this.setupFormArrayChanges();
    this.initializeDataSources();
  }

  private initializeFieldGroup(): void {
    if (!this.config) {
      console.error('FieldGroupConfig is required');
      return;
    }

    // Store data source configs
    this.config.fields.forEach((field) => {
      if (field.dataSource) {
        this.fieldDataSourceConfigs.set(field.id, field.dataSource);
      }
    });

    // Set default configuration values
    this.config.addButtonText = this.config.addButtonText ?? 'Add Item';
    this.config.removeButtonText = this.config.removeButtonText ?? 'Remove';
    this.config.showInstanceNumber = this.config.showInstanceNumber !== false;

    const existingValues = this.formArray.value;

    if (existingValues && existingValues.length > 0) {
      // Sync instances for existing data (edit mode)
      this.syncInstances();

      // CRITICAL FIX: Load ALL data sources for existing instances (including dependent ones)
      this.loadAllDataSourcesForExistingInstances();
    } else {
      const minInstances = this.config.minInstances ?? 0;
      if (minInstances > 0) {
        for (let i = 0; i < minInstances; i++) {
          this.addInstance();
        }
      } else {
        this.syncInstances();
      }
    }
  }

  /**
   * NEW: Load all data sources (including dependent ones) for existing instances.
   * This is called when the form array has initial values (edit mode).
   */
  private loadAllDataSourcesForExistingInstances(): void {
    this.instances.forEach((instance, index) => {
      const instanceValue = this.getInstanceControl(index).value;

      // Load all fields with data sources, in dependency order
      this.loadDataSourcesInOrder(index, instanceValue);

      // Mark this instance as initialized
      this.initializedInstances.add(index);

      // Store initial values for change detection
      this.previousInstanceValues[index] = { ...instanceValue };
    });
  }

  /**
   * NEW: Load data sources for an instance in the correct order.
   * Non-dependent fields first, then dependent fields.
   */
  private loadDataSourcesInOrder(instanceIndex: number, instanceValue: any): void {
    // First, load fields without dependencies
    const independentFields = this.config.fields.filter(
      (field) => field.dataSource && (!field.dependsOn || field.dependsOn.length === 0)
    );

    independentFields.forEach((field) => {
      this.loadFieldData(field.id, field.dataSource!, instanceValue, instanceIndex);
    });

    // Then, load fields with dependencies (if their dependencies are satisfied)
    const dependentFields = this.config.fields.filter(
      (field) => field.dataSource && field.dependsOn && field.dependsOn.length > 0
    );

    dependentFields.forEach((field) => {
      // Check if all dependencies have values
      const allDependenciesMet = field.dependsOn!.every((dep) => {
        const depValue = instanceValue[dep];
        return depValue !== null && depValue !== undefined && depValue !== '';
      });

      if (allDependenciesMet) {
        console.log(
          `[FieldGroup] Loading dependent field ${field.id} for instance ${instanceIndex}`
        );
        this.loadFieldData(field.id, field.dataSource!, instanceValue, instanceIndex);
      }
    });
  }

  private setupFormArrayChanges(): void {
    this.formArray.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((values) => {
      // Handle dependent fields for each instance
      values.forEach((instanceValue: any, index: number) => {
        this.handleDependentFields(instanceValue, index);
      });
    });
  }

  private initializeDataSources(): void {
    // Load initial data for fields that don't depend on other fields
    // Note: For existing instances, this is already handled by loadAllDataSourcesForExistingInstances
    this.config.fields.forEach((field) => {
      if (
        field.dataSource &&
        (!field.dataSource.dependsOnFields || field.dataSource.dependsOnFields.length === 0)
      ) {
        this.loadFieldDataForAllInstances(field.id, field.dataSource);
      }
    });
  }

  private handleDependentFields(instanceValue: any, instanceIndex: number): void {
    this.config.fields.forEach((field) => {
      if (field.dependsOn && field.dependsOn.length > 0) {
        const dependentChanged = field.dependsOn.some((dep) => {
          const prevValue = this.previousInstanceValues?.[instanceIndex]?.[dep];
          const currentValue = instanceValue[dep];
          return prevValue !== currentValue;
        });

        if (dependentChanged) {
          // Clear the current field value when dependency changes
          const control = this.getInstanceControl(instanceIndex).get(field.formControlName);
          if (control && field.dataSource?.dependsOnFields) {
            control.setValue(null, { emitEvent: false });
          }

          // Call onDependencyChange if provided
          if (field.onDependencyChange) {
            field.onDependencyChange(instanceValue, field.id, instanceIndex);
          }

          // Reload data source if field has one
          if (field?.dataSource?.dependsOnFields) {
            // Check if dependencies are satisfied
            const allDependenciesMet = field.dependsOn.every((dep) => {
              const depValue = instanceValue[dep];
              return depValue !== null && depValue !== undefined && depValue !== '';
            });

            if (allDependenciesMet) {
              // Add a small delay to ensure the DOM updates
              setTimeout(() => {
                this.loadFieldData(field.id, field.dataSource!, instanceValue, instanceIndex);
              }, 0);
            } else {
              // Clear the field's data source if dependencies not met
              this.updateFieldDataSource(field.id, [], instanceIndex);
            }
          }
        }
      }
    });

    // Store current values for next comparison
    this.previousInstanceValues = this.previousInstanceValues || {};
    this.previousInstanceValues[instanceIndex] = { ...instanceValue };
  }

  private loadFieldDataForAllInstances(fieldId: string, dataSource: DataSourceConfig): void {
    this.instances.forEach((instance, index) => {
      // Skip if this instance was already initialized with all data
      if (this.initializedInstances.has(index)) {
        return;
      }

      const instanceValue = this.getInstanceControl(index).value;
      this.loadFieldData(fieldId, dataSource, instanceValue, index);
    });
  }

  private loadFieldData(
    fieldId: string,
    dataSource: DataSourceConfig,
    formValue: any,
    instanceIndex: number
  ): void {
    this.fieldDataService
      .loadData(fieldId, dataSource, { formValue, instanceIndex })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.updateFieldDataSource(fieldId, data, instanceIndex);
        },
        error: (error) => {
          console.error(
            `Error loading data for field ${fieldId} at instance ${instanceIndex}:`,
            error
          );
        },
      });
  }

  private updateFieldDataSource(fieldId: string, data: any[], instanceIndex: number): void {
    const field = this.config.fields.find((f) => f.id === fieldId);
    if (field?.config) {
      // Create a new config object to trigger change detection
      field.config = {
        ...field.config,
        data: data,
        instanceData: {
          ...(field.config.instanceData ?? {}),
          [instanceIndex]: data,
        },
      };

      const control = this.getInstanceControl(instanceIndex).get(field.formControlName);
      if (control) {
        control.updateValueAndValidity({ emitEvent: false });
      }
    }
    this.cdr.markForCheck();
  }

  private syncInstances(): void {
    const currentLength = this.formArray.length;
    const newInstances: FieldGroupInstance[] = [];

    for (let i = 0; i < currentLength; i++) {
      const existingInstance = this.instances[i];
      if (existingInstance) {
        newInstances.push(existingInstance);
      } else {
        newInstances.push(this.createInstanceMetadata(i));
      }
    }

    this.instances = newInstances;
  }

  addInstance(): void {
    if (!this.canAddInstance) {
      return;
    }

    const newGroup = this.createFormGroup();
    this.formArray.push(newGroup);

    const instance = this.createInstanceMetadata(this.instances.length);
    this.instances.push(instance);

    // Load data sources for the new instance
    const instanceIndex = this.instances.length - 1;
    this.config.fields.forEach((field) => {
      if (field.dataSource && (!field.dependsOn || field.dependsOn.length === 0)) {
        const instanceValue = newGroup.value;
        this.loadFieldData(field.id, field.dataSource, instanceValue, instanceIndex);
      }
    });

    this.cdr.markForCheck();
  }

  removeInstance(index: number): void {
    if (!this.canRemoveInstance) {
      return;
    }

    this.formArray.removeAt(index);
    this.instances.splice(index, 1);

    // Update instance indices
    this.instances.forEach((instance, i) => {
      instance.index = i;
    });

    // Clean up tracking data
    this.initializedInstances.delete(index);
    delete this.previousInstanceValues[index];

    // Re-index the initialized instances set
    const newInitializedInstances = new Set<number>();
    this.initializedInstances.forEach((idx) => {
      if (idx > index) {
        newInitializedInstances.add(idx - 1);
      } else if (idx < index) {
        newInitializedInstances.add(idx);
      }
    });
    this.initializedInstances = newInitializedInstances;

    this.cdr.markForCheck();
  }

  toggleInstanceCollapse(index: number): void {
    if (this.config.collapsible) {
      this.instances[index].collapsed = !this.instances[index].collapsed;
      this.cdr.markForCheck();
    }
  }

  private createFormGroup(): FormGroup {
    const groupControls: { [key: string]: any } = {};

    this.config.fields.forEach((field) => {
      const validators = this.getValidators(field);
      const initialValue = field.value !== undefined ? field.value : null;
      groupControls[field.formControlName] = [initialValue, validators];
    });

    return this.fb.group(groupControls);
  }

  private getValidators(field: FormField): any[] {
    const validators: any[] = [];

    if (!field.validation) {
      return validators;
    }

    if (field.validation.required || field.config?.required) {
      validators.push(Validators.required);
    }

    if (field.validation.minLength) {
      validators.push(Validators.minLength(field.validation.minLength));
    }

    if (field.validation.maxLength) {
      validators.push(Validators.maxLength(field.validation.maxLength));
    }

    if (field.validation.min !== undefined) {
      validators.push(Validators.min(field.validation.min));
    }

    if (field.validation.max !== undefined) {
      validators.push(Validators.max(field.validation.max));
    }

    if (field.validation.pattern) {
      validators.push(Validators.pattern(field.validation.pattern));
    }

    if (field.validation.email) {
      validators.push(Validators.email);
    }

    return validators;
  }

  private createInstanceMetadata(index: number): FieldGroupInstance {
    return {
      id: `instance-${this.instanceIdCounter++}`,
      index,
      formGroupName: `${index}`,
      collapsed: false,
    };
  }

  getInstanceControl(index: number): FormGroup {
    return this.formArray.at(index) as FormGroup;
  }

  getInstanceTitle(index: number): string {
    if (this.config.groupTitle) {
      return this.config.groupTitle.replace('{{index}}', (index + 1).toString());
    }
    return this.config.showInstanceNumber ? `#${index + 1}` : '';
  }

  isFieldVisible(field: FormField, instanceIndex: number): boolean {
    if (field.visible === undefined || field.visible === true) {
      return true;
    }
    if (typeof field.visible === 'boolean') {
      return field.visible;
    }
    if (typeof field.visible === 'function') {
      const instanceValue = this.getInstanceControl(instanceIndex).value;
      return field.visible(instanceValue);
    }
    return true;
  }

  getGridClass(field: FormField): string {
    const position = field.gridPosition;
    if (!position) return 'col-12';

    if (position.colClass) {
      return position.colClass;
    }

    const colSpan = position.colSpan ?? 12;
    return `col-md-${colSpan}`;
  }

  /**
   * Get field config with instance-specific data.
   * This ensures each FormArray instance gets its own dropdown data.
   */
  getFieldConfigForInstance(field: FormField, instanceIndex: number): any {
    // Return empty object if no config
    if (!field.config) {
      return {};
    }

    // If there's instance-specific data, use it
    if (field.config.instanceData && field.config.instanceData[instanceIndex] !== undefined) {
      return {
        ...field.config,
        data: field.config.instanceData[instanceIndex],
      };
    }

    // Otherwise return the shared config
    return field.config;
  }

  trackByInstance(index: number, instance: FieldGroupInstance): string {
    return instance.id;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
